import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  Breadcrumbs,
  Button,
  ButtonGroup,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  TextField,
} from "@mui/material";
import * as services from "./../../services/saleng";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { jwtDecode } from "jwt-decode";

const AddUser = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    name: "",
    lastname: "",
    branchId: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [branches, setBranches] = useState([]);
  const [shopId, setShopId] = useState(null);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);

  const validate = () => {
    const newErrors = {};
    if (!formData.username.trim())
      newErrors.username = "กรุณากรอกชื่อผู้ใช้งาน";

    if (!formData.password.trim()) newErrors.password = "กรุณากรอกรหัสผ่าน";
    else if (formData.password.length < 8)
      newErrors.password = "รหัสผ่านต้องมีความยาวอย่างน้อย 8 ตัวอักษร";
    if (!formData.confirmPassword.trim())
      newErrors.confirmPassword = "กรุณากรอกยืนยันรหัสผ่าน";
    if (formData.password !== formData.confirmPassword)
      newErrors.confirmPassword = "รหัสผ่านและยืนยันรหัสผ่านไม่ตรงกัน";
    if (!formData.name.trim()) newErrors.name = "กรุณากรอกชื่อ";
    if (!formData.lastname.trim()) newErrors.lastname = "กรุณากรอกนามสกุล";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "", // ลบข้อความ error เมื่อผู้ใช้เริ่มกรอกข้อมูล
    }));
  };

  const handleSave = (back) => {
    if (!validate()) {
      return;
    }

    const updatedFormData = {
      ...formData,
      branchId: formData.branchId === "" ? null : formData.branchId,
    };

    services
      .createUser(updatedFormData, shopId)
      .then((response) => {
        console.log("response", response);
        if (back) {
          navigate("/users");
        } else {
          window.location.reload();
        }
      })
      .catch((error) => {
        toast.error(error.message);
        console.error("Error creating user:", error);
      });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!shopId) return;

      try {
        const shopResponse = await services.getShop(shopId);
        const shopData = shopResponse.data.data;

        const updatedBranches = shopData.branches.map((branch, index) => ({
          ...branch,
          key: `branch-${index}`,
          value: branch.id,
          label: branch.name,
        }));

        setBranches(updatedBranches);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      try {
        const decoded = jwtDecode(accessToken);
        if (decoded.id) {
          setShopId(decoded.id); // อัพเดตค่า shopId ผ่าน state
        }
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }

    fetchData();
  }, [shopId]);


  return (
    <>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 2, mb: 2 }}>
        <Link underline="hover" color="inherit" href="/">
          หน้าแรก
        </Link>
        <Link underline="hover" color="inherit" href="/users">
          จัดการพนักงาน
        </Link>
        <Typography color="text.primary">เพิ่มพนักงาน</Typography>
      </Breadcrumbs>

      <Toaster position="bottom-left" reverseOrder={false} />
      <Typography component="h2" variant="h5" sx={{ mb: 2 }}>
        เพิ่มพนักงาน
      </Typography>
      <Paper sx={{ p: 2 }}>
        <Box
          component="form"
          sx={{ "& .MuiTextField-root": { mt: 1, mb: 1, width: "100%" } }}
          noValidate
        >
          <Typography>ชื่อผู้ใช้งาน*</Typography>
          <TextField
            required
            id="username"
            name="username"
            placeholder="ชื่อผู้ใช้งาน"
            variant="outlined"
            value={formData.username}
            onChange={handleChange}
            error={!!errors.username}
            helperText={errors.username}
            inputProps={{
              autoComplete: "new-password",
            }}
            size="small"
          />
          <Typography>รหัสผ่าน*</Typography>
          <TextField
            required
            id="password"
            name="password"
            placeholder="รหัสผ่าน"
            variant="outlined"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleClickShowPassword} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            inputProps={{
              autoComplete: "new-password", // เพิ่ม autocomplete
            }}
            size="small"
          />
          <Typography>ยืนยันรหัสผ่าน*</Typography>
          <TextField
            required
            id="confirmPassword"
            name="confirmPassword"
            placeholder="ยืนยันรหัสผ่าน"
            variant="outlined"
            type={showConfirmPassword ? "text" : "password"}
            value={formData.confirmPassword}
            onChange={handleChange}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowConfirmPassword}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            inputProps={{
              autoComplete: "new-password",
            }}
            size="small"
          />
          <Typography>ชื่อ*</Typography>
          <TextField
            required
            id="name"
            name="name"
            placeholder="ชื่อ"
            variant="outlined"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            size="small"
          />
          <Typography>นามสกุล*</Typography>
          <TextField
            required
            id="lastname"
            name="lastname"
            placeholder="นามสกุล"
            variant="outlined"
            value={formData.lastname}
            onChange={handleChange}
            error={!!errors.lastname}
            helperText={errors.lastname}
            size="small"
          />
          <Typography>สาขา</Typography>
          <TextField
            id="branchId"
            name="branchId"
            select
            placeholder="สาขา"
            variant="outlined"
            value={formData.branchId || ""}
            onChange={handleChange}
            error={!!errors.branchId}
            helperText={errors.branchId}
            size="small"
          >
            <MenuItem value="" disabled>
              สาขา
            </MenuItem>
            {branches.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <ButtonGroup
          variant="contained"
          aria-label="Basic button group"
          sx={{ mt: 2 }}
          color="success"
        >
          <Button onClick={() => handleSave(true)} color="">
            บันทึกและกลับ
          </Button>
          <Button onClick={() => handleSave(false)}>บันทึก</Button>
        </ButtonGroup>
      </Paper>
    </>
  );
};

export default AddUser;